import React, {ReactNode, useEffect, useMemo} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Badge} from "@pg-design/badge-module";
import {IFormProps} from "@pg-mono/form";
import {FILTERS_TEST_IDS} from "@pg-mono/test-ids";

import {HOTLINE_SIDEBAR_FILTERS_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {hitFiltersInteraction} from "../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../real_estate/utils/convert_search_form_to_filters_tracking";
import {Currency} from "../../types/Currency";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {ISearchFiltersSlot} from "../types/ISearchFiltersSlot";
import {IRealEstateFiltersConfig} from "../types/RealEstateFiltersConfig";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {validateFilterError} from "../utils/filters/validate_filter";
import {FilterFooter} from "./filters/SearchFiltersFooter";
import {RealEstateFilters} from "./generic_filters/RealEstateFilters";
import {HotlineInfo} from "./HotlineInfo";

interface IProps {
    searchFiltersSlots: ISearchFiltersSlot[];
    searchFiltersCountBySlots?: Record<string, number>;
    legacyFormProps: IFormProps<ISearchFiltersFormValues>;
    onClose?: () => void;
    onResetForm?: () => void;
    footerClearButtonText?: string;
    footerSubmitButtonText?: string;
    isNotificationsSubscriptionModal?: boolean;
    currency?: Currency;
    filtersConfig: IRealEstateFiltersConfig;
}

export const SearchFiltersWithAccordion = (props: IProps) => {
    const {
        searchFiltersSlots,
        searchFiltersCountBySlots,
        legacyFormProps,
        onClose,
        onResetForm,
        footerClearButtonText,
        footerSubmitButtonText,
        isNotificationsSubscriptionModal,
        currency,
        filtersConfig
    } = props;

    useEffect(() => {
        hitFiltersInteraction("open", convertSearchFormToFiltersTracking(legacyFormProps.values as SearchFormValuesType));
    }, []);

    const onDrawerClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const submit = (e: React.MouseEvent<HTMLElement>) => {
        if (validateFilterError(legacyFormProps.values, searchFiltersSlots)) {
            // prevent form submission if there is a validation error
            return;
        }
        hitFiltersInteraction("apply", convertSearchFormToFiltersTracking(legacyFormProps.values as SearchFormValuesType));
        legacyFormProps.onSubmit && legacyFormProps.onSubmit(e);
        onDrawerClose();
    };

    return (
        <div className={filtersHolder}>
            <div className={cx(filtersScroller, isNotificationsSubscriptionModal && filtersSubscriptionStyle)}>
                <RealEstateFilters
                    formValues={legacyFormProps.values}
                    filtersConfig={filtersConfig}
                    filtersSlots={searchFiltersSlots}
                    legacyFormProps={legacyFormProps}
                    currency={currency}
                    filterRender={(filerSlot, filter) => (
                        <FilterAccordion
                            key={filerSlot.id}
                            legacyFormProps={legacyFormProps}
                            filterSlot={filerSlot}
                            filter={filter}
                            filterCount={searchFiltersCountBySlots?.[filerSlot.id]}
                        />
                    )}
                />

                <HotlineInfo hotlinePhoneNumber={HOTLINE_SIDEBAR_FILTERS_PHONE_NUMBER} className={css({md: {maxW: "100% !important"}})} />

                <div className={filterFooterWrapper}>
                    <FilterFooter
                        onCancel={onResetForm || (() => null)}
                        onSave={(e: React.MouseEvent<HTMLElement>) => {
                            submit(e);
                        }}
                        clearText={footerClearButtonText}
                        submitText={footerSubmitButtonText}
                    />
                </div>
            </div>
        </div>
    );
};

interface IFilterAccordionProps {
    legacyFormProps: IFormProps<ISearchFiltersFormValues>;
    filterSlot: ISearchFiltersSlot;
    filter: ReactNode;
    filterCount?: number;
}

const FilterAccordion = (props: IFilterAccordionProps) => {
    const {filterSlot, filterCount, legacyFormProps, filter} = props;

    const filterId = filterSlot.id;

    const isInitiallyExpanded = useMemo(() => {
        switch (filterId) {
            case "price":
                return legacyFormProps.values[filterId].upper === "" && !legacyFormProps.values.instalment;
            case "rooms":
                return legacyFormProps.values[filterId].upper === "";
            default:
                return false;
        }
    }, []);

    return (
        <Accordion key={filterSlot.id} initiallyExpanded={isInitiallyExpanded} theme="white">
            <Accordion.Header>
                <div
                    className={filterHeaderTextStyle}
                    data-testid={
                        filterSlot.label === "Metraż"
                            ? FILTERS_TEST_IDS.AREA
                            : filterSlot.label === "Termin oddania"
                              ? FILTERS_TEST_IDS.DELIVERY_DATE
                              : undefined
                    }
                >
                    {filterSlot.label}
                </div>
                {filterCount ? (
                    <Badge variant="label_danger" className={filtersCountBadge}>
                        {filterCount}
                    </Badge>
                ) : null}
            </Accordion.Header>
            <Accordion.Content className={css({pb: 0.5})}>{filter}</Accordion.Content>
        </Accordion>
    );
};

const filtersHolder = css({
    top: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flexBasis: "100%"
});

const filtersScroller = css({
    display: "flex",
    flexDirection: "column",
    p: 2,
    gap: 2,
    pb: 0,
    px: 2,
    overflow: "auto",
    flex: "1 1 auto"
});

const filtersSubscriptionStyle = css({
    md: {
        minHeight: "566px",
        p: 0,

        "& > div": {
            p: 0,
            pb: 0
        }
    }
});

const filterFooterWrapper = css({
    p: 2,
    flex: "0 0",
    position: "sticky",
    bottom: 0,
    bg: "var(--colors-background-100)",
    mb: "-2rem",
    zIndex: 11
});

const filterHeaderTextStyle = css({
    flex: "1 1 100%"
});

const filtersCountBadge = css({
    flex: "0 0 auto",
    justifySelf: "flex-end",
    justifyContent: "center",
    mr: 2,
    p: 0.5,
    minW: "2.2rem"
});
import React, {ReactNode, useEffect, useMemo} from "react";
import {css, cx} from "@panda/css";

import {Accordion} from "@pg-design/accordion-module";
import {Badge} from "@pg-design/badge-module";
import {IFormProps} from "@pg-mono/form";
import {FILTERS_TEST_IDS} from "@pg-mono/test-ids";

import {HOTLINE_SIDEBAR_FILTERS_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {hitFiltersInteraction} from "../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../real_estate/utils/convert_search_form_to_filters_tracking";
import {Currency} from "../../types/Currency";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {ISearchFiltersSlot} from "../types/ISearchFiltersSlot";
import {IRealEstateFiltersConfig} from "../types/RealEstateFiltersConfig";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {validateFilterError} from "../utils/filters/validate_filter";
import {FilterFooter} from "./filters/SearchFiltersFooter";
import {RealEstateFilters} from "./generic_filters/RealEstateFilters";
import {HotlineInfo} from "./HotlineInfo";

interface IProps {
    searchFiltersSlots: ISearchFiltersSlot[];
    searchFiltersCountBySlots?: Record<string, number>;
    legacyFormProps: IFormProps<ISearchFiltersFormValues>;
    onClose?: () => void;
    onResetForm?: () => void;
    footerClearButtonText?: string;
    footerSubmitButtonText?: string;
    isNotificationsSubscriptionModal?: boolean;
    currency?: Currency;
    filtersConfig: IRealEstateFiltersConfig;
}

export const SearchFiltersWithAccordion = (props: IProps) => {
    const {
        searchFiltersSlots,
        searchFiltersCountBySlots,
        legacyFormProps,
        onClose,
        onResetForm,
        footerClearButtonText,
        footerSubmitButtonText,
        isNotificationsSubscriptionModal,
        currency,
        filtersConfig
    } = props;

    useEffect(() => {
        hitFiltersInteraction("open", convertSearchFormToFiltersTracking(legacyFormProps.values as SearchFormValuesType));
    }, []);

    const onDrawerClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const submit = (e: React.MouseEvent<HTMLElement>) => {
        if (validateFilterError(legacyFormProps.values, searchFiltersSlots)) {
            // prevent form submission if there is a validation error
            return;
        }
        hitFiltersInteraction("apply", convertSearchFormToFiltersTracking(legacyFormProps.values as SearchFormValuesType));
        legacyFormProps.onSubmit && legacyFormProps.onSubmit(e);
        onDrawerClose();
    };

    return (
        <div className={filtersHolder}>
            <div className={cx(filtersScroller, isNotificationsSubscriptionModal && filtersSubscriptionStyle)}>
                <RealEstateFilters
                    formValues={legacyFormProps.values}
                    filtersConfig={filtersConfig}
                    filtersSlots={searchFiltersSlots}
                    legacyFormProps={legacyFormProps}
                    currency={currency}
                    filterRender={(filerSlot, filter) => (
                        <FilterAccordion
                            key={filerSlot.id}
                            legacyFormProps={legacyFormProps}
                            filterSlot={filerSlot}
                            filter={filter}
                            filterCount={searchFiltersCountBySlots?.[filerSlot.id]}
                        />
                    )}
                />

                <HotlineInfo hotlinePhoneNumber={HOTLINE_SIDEBAR_FILTERS_PHONE_NUMBER} className={css({md: {maxW: "100% !important"}})} />

                <div className={filterFooterWrapper}>
                    <FilterFooter
                        onCancel={onResetForm || (() => null)}
                        onSave={(e: React.MouseEvent<HTMLElement>) => {
                            submit(e);
                        }}
                        clearText={footerClearButtonText}
                        submitText={footerSubmitButtonText}
                    />
                </div>
            </div>
        </div>
    );
};

interface IFilterAccordionProps {
    legacyFormProps: IFormProps<ISearchFiltersFormValues>;
    filterSlot: ISearchFiltersSlot;
    filter: ReactNode;
    filterCount?: number;
}

const FilterAccordion = (props: IFilterAccordionProps) => {
    const {filterSlot, filterCount, legacyFormProps, filter} = props;

    const filterId = filterSlot.id;

    const isInitiallyExpanded = useMemo(() => {
        switch (filterId) {
            case "price":
                return legacyFormProps.values[filterId].upper === "" && !legacyFormProps.values.instalment;
            case "rooms":
                return legacyFormProps.values[filterId].upper === "";
            default:
                return false;
        }
    }, []);

    return (
        <Accordion key={filterSlot.id} initiallyExpanded={isInitiallyExpanded} theme="white">
            <Accordion.Header>
                <div
                    className={filterHeaderTextStyle}
                    data-testid={
                        filterSlot.label === "Metraż"
                            ? FILTERS_TEST_IDS.AREA
                            : filterSlot.label === "Termin oddania"
                              ? FILTERS_TEST_IDS.DELIVERY_DATE
                              : undefined
                    }
                >
                    {filterSlot.label}
                </div>
                {filterCount ? (
                    <Badge variant="label_danger" className={filtersCountBadge}>
                        {filterCount}
                    </Badge>
                ) : null}
            </Accordion.Header>
            <Accordion.Content className={css({pb: 0.5})}>{filter}</Accordion.Content>
        </Accordion>
    );
};

const filtersHolder = css({
    top: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flexBasis: "100%"
});

const filtersScroller = css({
    display: "flex",
    flexDirection: "column",
    p: 2,
    gap: 2,
    pb: 0,
    px: 2,
    overflow: "auto",
    flex: "1 1 auto"
});

const filtersSubscriptionStyle = css({
    md: {
        minHeight: "566px",
        p: 0,

        "& > div": {
            p: 0,
            pb: 0
        }
    }
});

const filterFooterWrapper = css({
    p: 2,
    flex: "0 0",
    position: "sticky",
    bottom: 0,
    bg: "var(--colors-background-100)",
    mb: "-2rem",
    zIndex: 11
});

const filterHeaderTextStyle = css({
    flex: "1 1 100%"
});

const filtersCountBadge = css({
    flex: "0 0 auto",
    justifySelf: "flex-end",
    justifyContent: "center",
    mr: 2,
    p: 0.5,
    minW: "2.2rem"
});
